import React, { useState, useEffect } from "react";
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquarePlus, faSquareMinus } from "@fortawesome/pro-light-svg-icons";
import {
  faGripDotsVertical,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/pro-solid-svg-icons";
import { faMessageCaptions, faGear } from "@fortawesome/pro-regular-svg-icons";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import { TextField } from "@components/FormsV2";
import {
  useCreateProjectServiceMutation,
  useDeleteProjectPhaseMutation,
  useDeleteProjectServiceMutation,
  useDeleteProjectSubserviceMutation,
  useUpdateProjectServiceMutation,
  useCreateProjectSubserviceMutation,
  useUpdateProjectSubserviceMutation,
  useUpdateProjectPhaseMutation,
  useDuplicateProjectServiceByIdMutation,
  useSyncrhonizeStandardProjectServiceByIdMutation,
  useRefreshProjectServiceByIdMutation,
} from "@generated";
import { toCamelCase } from "@utils/helpers";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import {
  ProjectService as Service,
  ProjectSubservice as Subservice,
  ProjectPhase as Phase,
  ColumnDef,
  Project,
} from "../../types";
import ManageNotesSlideout from "../ManageNotesSlideout";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";

interface Props {
  phases: Phase[];
  setPhases: (phases: Phase[]) => void;
  prevPhasesRef: React.MutableRefObject<Phase[]>;
  refetchPhases: () => void;
  services: Service[];
  setServices: (services: Service[]) => void;
  prevServicesRef: React.MutableRefObject<Service[]>;
  refetchServices: () => void;
  columnDefs: ColumnDef<Service>[];
  service_type: "professional_services" | "managed_services";
  draggable?: boolean;
  selectable?: boolean;
  showNotes?: boolean;
  showSettings?: boolean;
  project?: Project;
}

export const ServiceEditorV2: React.FC<Props> = ({
  phases,
  setPhases,
  prevPhasesRef,
  refetchPhases = () => null,
  services,
  setServices,
  prevServicesRef,
  refetchServices = () => null,
  columnDefs,
  draggable = false,
  selectable = false,
  service_type,
  showNotes = false,
  showSettings = false,
  project,
}) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const isManagedServices = service_type === "managed_services";
  //activeId => dragged Item
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggingPhase, setDraggingPhase] = useState<boolean>(false);
  const [collapsedPhases, setCollapsedPhases] = useState<
    Record<number, boolean>
  >({});
  const [openMenuItem, setOpenMenuItem] =
    useState<Phase | Service | null>(null);
  const [showRenamePhaseModal, setShowRenamePhaseModal] =
    useState<boolean>(false);
  const [newPhaseName, setNewPhaseName] = useState<string>("");
  const [showDeletePhaseModal, setShowDeletePhaseModal] =
    useState<boolean>(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string>("");
  const [updatePhase] = useUpdateProjectPhaseMutation();
  const [deletePhase] = useDeleteProjectPhaseMutation();
  const [updateService] = useUpdateProjectServiceMutation();
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    if (draggingPhase) {
      requestAnimationFrame(() => {
        const newCollapsed: Record<string, boolean> = {};
        phases.forEach((p) => (newCollapsed[p.id] = true));
        setCollapsedPhases(newCollapsed);
      });
    }
  }, [draggingPhase]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id.toString());

    const [type] = event.active.id.toString().split(":");
    if (type === "phase") {
      setDraggingPhase(true);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setDraggingPhase(false);
    if (!over) return;

    const [type, id] = active.id.toString().split(":");
    const [overType, overId] = over.id.toString().split(":");

    // === Phase Reorder ===
    if (type === "phase" && overType === "phase") {
      const oldIndex = phases.findIndex((p) => p.id === id);
      const newIndex = phases.findIndex((p) => p.id === overId);
      const reordered = arrayMove(phases, oldIndex, newIndex).map((p, i) => ({
        ...p,
        position: i + 1,
      }));
      setPhases(reordered);
      const prevPhases = prevPhasesRef?.current;
      const changed = reordered.filter(
        (newPhase) =>
          newPhase.position !==
          prevPhases?.find((p) => p.id === newPhase.id)?.position
      );

      if (!changed.length) return;

      for (const phase of changed) {
        const data = {
          id: Number(phase.id),
          type: "project-phases",
          attributes: {
            position: phase.position,
          },
        };
        const response = await updatePhase({
          slug: accountSlug,
          id: Number(phase.id),
          //@ts-ignore
          body: { data },
        });
      }
      // Save the latest order to reference
      prevPhasesRef.current = [...phases];
      return;
    }

    // === Service Reorder ===
    if (type === "service") {
      const activeService = services.find((s) => s.id === id);
      if (!activeService) return;

      let updated = [...services];
      // Dropped on a phase container
      if (overType === "phase") {
        updated = services.map((s) =>
          s.id === activeService.id
            ? { ...s, phase: overId, position: 9999 }
            : s
        );
      }

      // Dropped on another service
      if (overType === "service") {
        const overService = services.find((s) => s.id === overId);
        if (!overService) return;

        const oldIndex = services.findIndex((s) => s.id === activeService.id);
        const newIndex = services.findIndex((s) => s.id === overService.id);
        updated = arrayMove(services, oldIndex, newIndex);

        // If dragged across phases, update phase
        if (activeService.phase !== overService.phase) {
          updated = updated.map((s) =>
            s.id === activeService.id ? { ...s, phase: overService.phase } : s
          );
        }
      }

      const normalized = normalizeServicePositions(updated);
      const prevServices = prevServicesRef?.current;
      const changed = normalized.filter(
        (service) =>
          service.position !==
          prevServices?.find((p) => p.id === service.id)?.position
      );

      if (!changed.length) return;

      // Save the latest order to reference
      prevServicesRef.current = [...normalized];
      setServices(normalized);

      for (const service of changed) {
        let data: {
          id: number;
          type: string;
          attributes: { position: number };
          relationships?: {
            "project-phase"?: { data: { id: string; type: string } };
          };
        } = {
          id: Number(service.id),
          type: "project-services",
          attributes: {
            position: service.position,
          },
        };
        if (!isManagedServices && service.phase) {
          data.relationships = {
            "project-phase": {
              data: { id: service.phase, type: "project-phases" },
            },
          };
        }
        const response = await updateService({
          slug: accountSlug,
          id: Number(data.id),
          //@ts-ignore
          body: { data },
        });
      }
    }
  };
  // Normalize service positions per phase
  const normalizeServicePositions = (items: Service[]) => {
    const grouped: Record<number, Service[]> = {};
    items.forEach((s) => {
      if (!grouped[s.phase]) grouped[s.phase] = [];
      grouped[s.phase].push(s);
    });
    const flattened: Service[] = [];
    Object.values(grouped).forEach((group) => {
      group.forEach((s, i) => {
        flattened.push({ ...s, position: i + 1 });
      });
    });
    return flattened;
  };

  // === DragOverlay Content ===
  const activeService = activeId?.startsWith("service:")
    ? services.find((s) => `service:${s.id}` === activeId)
    : null;
  const activePhase = activeId?.startsWith("phase:")
    ? phases.find((p) => `phase:${p.id}` === activeId)
    : null;

  const getPhaseMenu = (phase) => {
    return (
      <div className="cogMenu">
        <div
          className="cogMenuItem"
          onClick={() => setShowRenamePhaseModal(true)}
        >
          Rename
        </div>
        {services?.find((s) => s.phase === phase.id) ? (
          <div className="cogMenuItem disabled">
            Remove all services to delete phase
          </div>
        ) : (
          <div
            className="cogMenuItem"
            onClick={() => setShowDeletePhaseModal(true)}
          >
            Delete
          </div>
        )}
      </div>
    );
  };
  const closePhaseModal = () => {
    setNewPhaseName("");
    setOpenMenuItem(null);
    setShowRenamePhaseModal(false);
    setShowDeletePhaseModal(false);
  };
  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={phases.map((p) => `phase:${p.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {!isManagedServices ? (
            phases.map((phase) => (
              <SortablePhase
                phase={phase}
                collapsedPhases={collapsedPhases}
                setCollapsedPhases={setCollapsedPhases}
                openMenuItem={openMenuItem}
                setOpenMenuItem={setOpenMenuItem}
                getPhaseMenu={getPhaseMenu}
                services={services}
                setServices={setServices}
                prevServicesRef={prevServicesRef}
                refetchServices={refetchServices}
                columnDefs={columnDefs}
                draggable={draggable}
                selectable={selectable}
                showSettings={showSettings}
                showNotes={showNotes}
                project={project}
              />
            ))
          ) : (
            <PhaseServiceTable
              services={
                services.length
                  ? services
                  : [
                      {
                        id: "-1",
                        name: "No managed services",
                        position: 1,
                        phase: "-1",
                        subservices: [],
                      },
                    ]
              }
              setServices={setServices}
              columnDefs={columnDefs}
              phaseId={"-1"}
              draggable={draggable}
              selectable={selectable}
              prevServicesRef={prevServicesRef}
              showSettings={showSettings}
              showNotes={showNotes}
              openMenuItem={openMenuItem}
              setOpenMenuItem={setOpenMenuItem}
              refetchServices={refetchServices}
              project={project}
            />
          )}
        </SortableContext>

        <DragOverlay adjustScale={false}>
          {activePhase ? (
            <PhaseOverlay phase={activePhase} />
          ) : activeService ? (
            <ServiceOverlay
              service={activeService}
              columnDefs={columnDefs}
              selectable={selectable}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      {/* Rename Phase Modal */}
      <ScopeStackModal
        modalTitle="Rename Phase"
        modalBody={
          <div>
            <div style={{ marginBottom: "15px" }}>
              Please provide the new phase name
            </div>
            <TextField
              id="phaseName"
              label="Phase Name"
              defaultValue={
                phases?.find((p) => String(p.id) === openMenuItem?.id)?.name
              }
              placeholder="Enter a name for this phase"
              onChange={(e) => setNewPhaseName(e.target.value)}
              required
            />
          </div>
        }
        handleClose={closePhaseModal}
        show={showRenamePhaseModal}
        button1Text="Cancel"
        handleButton1Click={closePhaseModal}
        button2Text="Rename"
        handleButton2Click={async () => {
          const data = {
            id: Number(openMenuItem?.id),
            type: "project-phases",
            attributes: { name: newPhaseName },
          };

          const response = await updatePhase({
            slug: accountSlug,
            id: data.id,
            //@ts-ignore
            body: { data },
          });

          //@ts-ignore
          if (response?.data?.data?.id) {
            refetchPhases();
            closePhaseModal();
            setResponseMessage("Phase renamed");
            setShowSuccessAlert(true);
          } else {
            setResponseMessage("Error saving phase name");
            setShowFailAlert(true);
          }
        }}
        button2Disabled={!newPhaseName}
        className="projectPhaseModal"
      />
      {/* Delete Phase Confimation */}
      <ConfirmationModal
        show={showDeletePhaseModal}
        title="Delete Phase"
        message="Are you sure you want to delete this phase?"
        onConfirm={async () => {
          const response = await deletePhase({
            slug: accountSlug,
            id: Number(openMenuItem?.id),
          });

          //@ts-ignore
          if (response?.error) {
            setResponseMessage("Error deleting phase");
            setShowFailAlert(true);
          } else {
            refetchPhases();
            closePhaseModal();
            setResponseMessage("Phase deleted");
            setShowSuccessAlert(true);
          }
        }}
        onCancel={closePhaseModal}
      />
      {handleAlert(
        showFailAlert,
        responseMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        responseMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
    </>
  );
};

// ===== Overlay Components =====
const PhaseOverlay = ({ phase }: { phase: Phase }) => (
  <div className="projectPhase dragging">
    <div className="phaseHeader">
      <div className="phaseTitle">
        <FontAwesomeIcon className="gripIcon" icon={faGripDotsVertical} />
        <div>{phase.name}</div>
        <FontAwesomeIcon className="gearIcon" icon={faGear} />
      </div>
      <FontAwesomeIcon className="expandIcon" icon={faChevronUp} />
    </div>
  </div>
);

const ServiceOverlay = ({
  service,
  columnDefs,
  selectable,
}: {
  service: Service;
  columnDefs: ColumnDef<Service>[];
  selectable: boolean;
}) => {
  return (
    <table className="serviceTable">
      <tbody>
        <tr className="projectService dragging">
          {columnDefs.map((col, i) => (
            <td key={col.id} className={`${toCamelCase(col.id)}Col`}>
              <div className="serviceTd">
                {i === 0 && (
                  <span className="dndHandle">
                    <FontAwesomeIcon icon={faGripDotsVertical} />
                  </span>
                )}
                {i === 0 && selectable && (
                  <input type="checkbox" checked={service.checked} readOnly />
                )}
                {i === 0 && service?.type === "project-services" && (
                  <span style={{ cursor: "default" }}>
                    <FontAwesomeIcon icon={faSquarePlus} size="sm" />
                  </span>
                )}
                {col.cell(service)}
              </div>
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
};

// ===== SortablePhase =====
const SortablePhase = ({
  phase,
  services,
  setServices,
  refetchServices,
  collapsedPhases,
  setCollapsedPhases,
  openMenuItem,
  setOpenMenuItem,
  getPhaseMenu,
  columnDefs,
  draggable,
  selectable,
  prevServicesRef,
  showSettings,
  showNotes,
  project,
}: {
  phase: Phase;
  services: Service[];
  setServices: (services) => void;
  refetchServices: () => void;
  collapsedPhases: Record<number, boolean>;
  setCollapsedPhases: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  openMenuItem: Service | Phase | null;
  setOpenMenuItem: (item) => void;
  getPhaseMenu: (phase) => void;
  columnDefs: ColumnDef<Service>[];
  draggable: boolean;
  selectable: boolean;
  prevServicesRef: React.MutableRefObject<Service[]>;
  showSettings: boolean;
  showNotes: boolean;
  project?: Project;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: `phase:${phase.id}` });
  const { hasPrivilege } = usePrivilegeCheck();
  const managePhasePrivilege = hasPrivilege("projects.project_phases", [
    "manage",
  ]);

  const isCollapsed = collapsedPhases[phase.id] ?? false;

  return (
    <div
      key={phase.id}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className="projectPhase"
    >
      <div className={`phaseHeader ${!managePhasePrivilege ? "noDnD" : ""}`}>
        <div className="phaseTitle">
          <div className="phaseDragHandle" {...attributes} {...listeners}>
            {managePhasePrivilege && draggable && (
              <FontAwesomeIcon className="gripIcon" icon={faGripDotsVertical} />
            )}
            <div>{phase.name}</div>
          </div>

          {managePhasePrivilege && showSettings && (
            <div style={{ position: "relative", marginLeft: "auto" }}>
              <>
                <FontAwesomeIcon
                  icon={faGear}
                  onClick={(e) => {
                    e.stopPropagation(); // prevent drag event interference
                    setOpenMenuItem((prev) => (prev ? null : phase));
                  }}
                  style={{ cursor: "pointer" }}
                />
                {openMenuItem?.type?.includes("phases") &&
                  openMenuItem?.id == phase.id &&
                  getPhaseMenu(phase)}
              </>
            </div>
          )}
        </div>
        <FontAwesomeIcon
          className="expandIcon"
          icon={collapsedPhases[phase.id] ? faChevronDown : faChevronUp}
          onClick={(e) => {
            e.stopPropagation();
            setCollapsedPhases((prev) => ({
              ...prev,
              [phase.id]: !isCollapsed,
            }));
          }}
        />
      </div>

      {!isCollapsed && (
        <div className="phaseBody">
          <PhaseServiceTable
            services={services}
            setServices={setServices}
            columnDefs={columnDefs}
            phaseId={phase.id}
            draggable={draggable}
            selectable={selectable}
            prevServicesRef={prevServicesRef}
            showSettings={showSettings}
            showNotes={showNotes}
            openMenuItem={openMenuItem}
            setOpenMenuItem={setOpenMenuItem}
            refetchServices={refetchServices}
            project={project}
          />
        </div>
      )}
    </div>
  );
};

const PhaseServiceTable = ({
  services,
  setServices,
  refetchServices,
  columnDefs,
  phaseId,
  draggable,
  selectable,
  prevServicesRef,
  showSettings,
  showNotes,
  openMenuItem,
  setOpenMenuItem,
  project,
}: {
  services: Service[];
  setServices: (services) => void;
  refetchServices: () => void;
  columnDefs: ColumnDef<Service>[];
  phaseId: string;
  draggable: boolean;
  selectable: boolean;
  prevServicesRef: React.MutableRefObject<Service[]>;
  showSettings: boolean;
  showNotes: boolean;
  openMenuItem: Service | Subservice | null;
  setOpenMenuItem: (item) => void;
  project?: Project;
}) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  const [updateSubservice] = useUpdateProjectSubserviceMutation();
  const [createSubservice] = useCreateProjectSubserviceMutation();
  const [createService] = useCreateProjectServiceMutation();
  const [deleteService] = useDeleteProjectServiceMutation();
  const [deleteSubservice] = useDeleteProjectSubserviceMutation();
  const [showDeleteServiceModal, setShowDeleteServiceModal] = useState(false);
  const [showSaveStandardModal, setShowSaveStandardModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] =
    useState<{ all_locations: boolean } | null>(null);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [openNotesItem, setOpenNotesItem] = useState<Service | null>(null);
  const [duplicateService] = useDuplicateProjectServiceByIdMutation();
  const [synchronizeStandard] =
    useSyncrhonizeStandardProjectServiceByIdMutation();
  const [refreshService] = useRefreshProjectServiceByIdMutation();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const { hasPrivilege } = usePrivilegeCheck();
  const manageServicePrivilege = hasPrivilege("projects.tasks", [
    "manage",
    "adjust_standards",
  ]);
  const createStandardPrivilege = hasPrivilege("settings.tasks", ["manage"]);

  const toggleExpand = (id: string) =>
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const isManagedServices = phaseId === "-1";

  const servicesOnPhase = isManagedServices
    ? services
    : services?.filter((s) => s.phase == phaseId) || [];

  const updateSubservicePosition = async (reordered, serviceId) => {
    const previous = prevServicesRef.current?.find(
      (s) => s.id == serviceId
    )?.subservices;
    prevServicesRef.current = prevServicesRef?.current?.map((s) => {
      if (s.id == serviceId) {
        return { ...s, subservices: reordered };
      }
      return s;
    });

    const changed = reordered.filter(
      (sub) => sub.position !== previous?.find((p) => p.id === sub.id)?.position
    );
    if (!changed.length) return;

    setServices(
      services.map((s) => {
        if (s.id == serviceId) {
          return { ...s, subservices: reordered };
        }
        return s;
      })
    );

    for (const sub of changed) {
      const data = {
        id: Number(sub.id),
        type: "project-subservices",
        attributes: {
          name: sub.name,
          position: sub.position,
        },
        relationships: {
          "project-service": {
            data: { id: serviceId, type: "project-services" },
          },
        },
      };

      const res = await updateSubservice({
        slug: accountSlug,
        id: Number(sub.id),
        //@ts-ignore
        body: { data },
      });
    }
  };

  const handleCreateService = async (serviceId) => {
    //determine service vs subservice
    const service = serviceId ? false : true;
    const isMS = service && window.location.href.includes("managed");
    const parentType = service ? "project-phase" : "project-service";
    const parentId = service ? phaseId : serviceId;
    const hook = service ? createService : createSubservice;
    let data = {
      type: `project-${service ? "services" : "subservices"}`,
      attributes: {
        name: " ",
        "service-type": isMS ? "managed_services" : undefined,
        "task-source": "custom",
      },
      relationships: {
        [parentType]: !isMS
          ? {
              data: {
                id: parentId,
                type: `${parentType}s`,
              },
            }
          : undefined,
      },
    };

    if (service)
      data.relationships.project = {
        data: { id: Number(project?.id), type: "projects" },
      };
    //@ts-ignore
    const response = await hook({ slug: accountSlug, body: { data } });
    //@ts-ignore
    const updated = response?.data?.data;
    if (updated) {
      const atts = updated?.attributes;
      const newService = {
        id: updated.id,
        type: updated.type,
        name: atts.name || " ",
        position: atts?.position || 99,
        quantity: atts?.quantity || "",
        hours: atts?.["total-hours"] || "0.0",
        phase: service ? phaseId : undefined,
      };
      setServices(
        service
          ? [...services, newService]
          : services?.map((s: Service) =>
              s.id == serviceId
                ? {
                    ...s,
                    subservices: [...s.subservices, newService],
                  }
                : s
            )
      );
      setResponseMessage(`${service ? "Service" : "Subservice"} added`);
      setShowSuccessAlert(true);
      //@ts-ignore
    } else if (response.error) {
      setResponseMessage(
        `Something went wrong adding a ${service ? "service" : "subservice"}`
      );
      setShowFailAlert(true);
    }
  };

  return (
    <>
      {servicesOnPhase?.length ? (
        <table className="serviceTable">
          <thead>
            <tr>
              {columnDefs.map((col) => (
                <th
                  key={col.id}
                  className={`tableCol ${toCamelCase(col.id)}Col`}
                >
                  {col.header()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {servicesOnPhase.map((service, i) => (
              <React.Fragment key={service.id}>
                <ServiceRow
                  service={service}
                  expanded={!!expandedRows[service.id]}
                  onToggleExpand={() => {
                    //prevent unintended autoscroll
                    const scrollY = window.scrollY;
                    requestAnimationFrame(() => {
                      window.scrollTo({ top: scrollY, behavior: "auto" });
                    });
                    toggleExpand(service.id);
                  }}
                  onSelect={(e) =>
                    setServices(
                      services.map((s) => {
                        if (s.id == service.id) {
                          return { ...s, checked: e.target.checked };
                        }
                        return s;
                      })
                    )
                  }
                  columnDefs={columnDefs}
                  draggable={draggable}
                  selectable={selectable}
                  striped={i % 2 ? true : false}
                  showSettings={showSettings}
                  menuItems={[
                    ...(manageServicePrivilege
                      ? [
                          {
                            label: "Add Subservice",
                            onClick: () => {
                              if (!expandedRows[service.id])
                                setExpandedRows({
                                  ...expandedRows,
                                  [service.id]: true,
                                });
                              setOpenMenuItem(null);
                              handleCreateService(service.id);
                            },
                          },
                        ]
                      : []),
                    ...(createStandardPrivilege
                      ? [
                          {
                            label: service?.standardId
                              ? "Update Existing Standard"
                              : "Save as Standard",
                            onClick: () => setShowSaveStandardModal(true),
                          },
                        ]
                      : []),
                    ...(service?.standardId
                      ? [
                          {
                            label: "Refresh",
                            onClick: () => setShowRefreshModal(true),
                          },
                        ]
                      : []),
                    ...(manageServicePrivilege
                      ? [
                          {
                            label: "Duplicate",
                            onClick: () =>
                              setShowDuplicateModal({ all_locations: false }),
                          },
                        ]
                      : []),
                    ...(manageServicePrivilege &&
                    project &&
                    project.locations?.length > 1
                      ? [
                          {
                            label: "Duplicate for all locations",
                            onClick: () =>
                              setShowDuplicateModal({ all_locations: true }),
                          },
                        ]
                      : []),
                    ...(manageServicePrivilege
                      ? [
                          {
                            label: "Delete",
                            onClick: () => setShowDeleteServiceModal(true),
                          },
                        ]
                      : []),
                  ]}
                  showNotes={showNotes}
                  openMenuItem={openMenuItem}
                  setOpenMenuItem={setOpenMenuItem}
                  setOpenNotesItem={setOpenNotesItem}
                />
                {expandedRows[service.id] && (
                  <tr className="subservicesRow">
                    <td colSpan={columnDefs.length}>
                      <div className="subservices">
                        <div className="subservicesHeader">Subservices</div>
                        <div className="subservicesTable">
                          <SubserviceTable
                            subservices={
                              service?.subservices?.length
                                ? service.subservices
                                : [
                                    {
                                      id: "-1",
                                      name: "No subservices on this service",
                                      position: 1,
                                    },
                                  ]
                            }
                            columnDefs={columnDefs
                              .filter((item) => item.id !== "service category")
                              .map((item) =>
                                item.id === "service name"
                                  ? {
                                      ...item,
                                      id: "subservice name",
                                      header: () => "SUBSERVICE NAME",
                                    }
                                  : item
                              )}
                            onReorderSubservices={async (e) =>
                              await updateSubservicePosition(e, service.id)
                            }
                            onSelect={(e, subId) =>
                              setServices(
                                services.map((s) => {
                                  if (s.id === service.id) {
                                    return {
                                      ...s,
                                      subservices: s.subservices?.map((sub) =>
                                        sub.id == subId
                                          ? {
                                              ...sub,
                                              checked: e.target.checked,
                                            }
                                          : sub
                                      ),
                                    };
                                  }
                                  return s;
                                })
                              )
                            }
                            draggable={draggable}
                            selectable={selectable}
                            showSettings={showSettings}
                            setShowDeleteServiceModal={
                              setShowDeleteServiceModal
                            }
                            openMenuItem={openMenuItem}
                            setOpenMenuItem={setOpenMenuItem}
                          />
                          {showSettings && manageServicePrivilege && (
                            <button
                              className="addServiceBtn"
                              onClick={() => {
                                if (!service?.id) return;
                                handleCreateService(service.id);
                              }}
                            >
                              + Add Subservice
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="placeholder">No services assigned to phase</div>
      )}
      {showSettings && manageServicePrivilege && (
        <button
          className="addServiceBtn"
          onClick={async () => await handleCreateService(undefined)}
        >
          + Add Custom Service
        </button>
      )}
      {/* Settings Menu Confirmation Modals */}
      <ScopeStackModal
        modalTitle="Save as Standard"
        modalBody={
          loading ? (
            <SmallSpinner />
          ) : (
            <>
              Are you sure you want to save{" "}
              <strong>{openMenuItem?.name}</strong> as standard?
            </>
          )
        }
        button1Text="Cancel"
        handleButton1Click={() => setShowSaveStandardModal(false)}
        button2Text="Save"
        handleButton2Click={async () => {
          if (!openMenuItem?.id) return;
          setLoading(true);
          const saveStandard = async () => {
            const response = await synchronizeStandard({
              slug: accountSlug,
              id: Number(openMenuItem.id),
            });
            //@ts-ignore
            if (response.error) {
              setResponseMessage(
                "Something went wrong saving this service to standards"
              );
              setShowFailAlert(true);
            } else {
              setResponseMessage("Saved as standard");
              setShowSuccessAlert(true);
            }
            setShowSaveStandardModal(false);
            setLoading(false);
            setOpenMenuItem(null);
          };
          await saveStandard();
        }}
        button2Disabled={loading}
        show={showSaveStandardModal}
        handleClose={() => setShowSaveStandardModal(false)}
      />
      <ScopeStackModal
        modalTitle="Refresh Service"
        modalBody={
          loading ? (
            <SmallSpinner />
          ) : (
            <>
              <div>
                Are you sure you want to refresh{" "}
                <strong>{openMenuItem?.name}</strong>?
              </div>
              <div style={{ marginTop: "10px" }}>
                This will refresh the name, service description, and language
                fields to match the standard service.
              </div>
            </>
          )
        }
        button1Text="Cancel"
        handleButton1Click={() => setShowRefreshModal(false)}
        button2Text="Refresh"
        handleButton2Click={async () => {
          if (!openMenuItem?.id) return;
          setLoading(true);
          const refresh = async () => {
            const response = await refreshService({
              slug: accountSlug,
              id: Number(openMenuItem.id),
              body: {
                data: {
                  id: Number(openMenuItem.id),
                  type: "project-services",
                },
              },
            });

            await refetchServices();

            //@ts-ignore
            if (response.error) {
              setResponseMessage(
                "Something went wrong refreshing this service"
              );
              setShowFailAlert(true);
            } else {
              setResponseMessage("Service contents refreshed");
              setShowSuccessAlert(true);
            }
            setShowRefreshModal(false);
            setLoading(false);
            setOpenMenuItem(null);
          };
          await refresh();
        }}
        button2Disabled={loading}
        show={showRefreshModal}
        handleClose={() => setShowRefreshModal(false)}
      />
      <ScopeStackModal
        modalTitle="Duplicate Service"
        modalBody={
          loading ? (
            <SmallSpinner />
          ) : (
            <>
              Are you sure you want to duplicate{" "}
              <strong>{openMenuItem?.name}</strong>{" "}
              {showDuplicateModal?.all_locations
                ? "for all project locations"
                : ""}
              ?
            </>
          )
        }
        button1Text="Cancel"
        handleButton1Click={() => setShowDuplicateModal(null)}
        button2Text="Duplicate"
        handleButton2Click={async () => {
          if (!openMenuItem?.id || !showDuplicateModal) return;
          setLoading(true);
          const duplicate = async () => {
            const response = await duplicateService({
              slug: accountSlug,
              id: Number(openMenuItem.id),
              body: {
                data: {
                  id: Number(openMenuItem.id),
                  type: "project-services",
                  attributes: showDuplicateModal,
                },
              },
            });

            await refetchServices();

            //@ts-ignore
            if (response.error) {
              setResponseMessage(
                "Something went wrong duplicating this service"
              );
              setShowFailAlert(true);
            } else {
              setResponseMessage(
                `Service duplicated ${
                  showDuplicateModal?.all_locations
                    ? "for all project locations"
                    : ""
                }`
              );
              setShowSuccessAlert(true);
            }
            setShowDuplicateModal(null);
            setLoading(false);
            setOpenMenuItem(null);
          };
          await duplicate();
        }}
        button2Disabled={loading}
        show={showDuplicateModal}
        handleClose={() => setShowDuplicateModal(null)}
      />
      <ScopeStackModal
        modalTitle={`Delete ${
          openMenuItem?.type?.includes("subservices") ? "Subservice" : "Service"
        }`}
        modalBody={
          loading ? (
            <SmallSpinner />
          ) : (
            <div>
              Are you sure you want to delete
              <strong>
                {" "}
                {openMenuItem?.name ||
                  `this ${
                    openMenuItem?.type?.includes("subservices")
                      ? "subservice"
                      : "service"
                  }`}
              </strong>
              ?
            </div>
          )
        }
        button1Text="Cancel"
        handleButton1Click={() => setShowDeleteServiceModal(false)}
        button2Text="Delete"
        handleButton2Click={async () => {
          if (!openMenuItem?.id) return;
          setLoading(true);
          const handleDelete = async () => {
            const isSubservice = openMenuItem?.type?.includes("subservices");
            const hook = isSubservice ? deleteSubservice : deleteService;
            const response = await hook({
              slug: accountSlug,
              id: Number(openMenuItem?.id),
            });

            await refetchServices();

            //@ts-ignore
            if (response.error) {
              setResponseMessage(
                "Something went wrong deleting this subservice"
              );
              setShowFailAlert(true);
            } else {
              setResponseMessage(
                `${isSubservice ? "Subservice" : "Service"} deleted`
              );
              setShowSuccessAlert(true);
            }
            setShowDeleteServiceModal(false);
            setLoading(false);
            setOpenMenuItem(null);
          };
          await handleDelete();
        }}
        button2Disabled={loading}
        show={showDeleteServiceModal}
        handleClose={() => setShowDeleteServiceModal(false)}
        deleteModal={true}
      />
      <ManageNotesSlideout
        resource={openNotesItem}
        onClose={() => {
          setOpenNotesItem(null);
          refetchServices();
        }}
      />

      {handleAlert(
        showFailAlert,
        responseMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        responseMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
    </>
  );
};

const ServiceRow = ({
  service,
  expanded,
  onToggleExpand,
  onSelect,
  columnDefs,
  draggable,
  selectable,
  striped,
  showSettings,
  menuItems,
  showNotes,
  openMenuItem,
  setOpenMenuItem,
  setOpenNotesItem,
}: {
  service: Service;
  expanded: boolean;
  onToggleExpand: () => void;
  onSelect: (e) => void;
  columnDefs: ColumnDef<Service>[];
  draggable: boolean;
  selectable: boolean;
  striped: boolean;
  showSettings: boolean;
  menuItems: { label: string; onClick: () => void }[];
  showNotes: boolean;
  openMenuItem: Service | Subservice | null;
  setOpenMenuItem: (item: Service | Subservice | null) => void;
  setOpenNotesItem: (item: Service | null) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: `service:${service.id}`,
    });
  const isPlaceholder = service?.id == "-1";

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        backgroundColor: striped ? "#FAFAFA" : "#fff",
      }}
    >
      {columnDefs.map((col, index) => {
        const isLast = index === columnDefs.length - 1;
        return (
          <td
            key={col.id}
            className={col?.id?.includes("name") ? "nameField" : ""}
          >
            <div className="serviceTd">
              {index == 0 && draggable && !isPlaceholder && (
                <span {...attributes} {...listeners} className="dndHandle">
                  <FontAwesomeIcon icon={faGripDotsVertical} />
                </span>
              )}
              {index == 0 && selectable && !isPlaceholder && (
                <input
                  type="checkbox"
                  style={{ accentColor: "#418172" }}
                  checked={service.checked}
                  onChange={onSelect}
                />
              )}
              {index == 0 && !isPlaceholder && (
                <FontAwesomeIcon
                  icon={expanded ? faSquareMinus : faSquarePlus}
                  size="lg"
                  className="serviceToggle"
                  onClick={onToggleExpand}
                />
              )}

              {col.cell(service)}

              {isLast && !isPlaceholder && (
                <div style={{ paddingLeft: "5px" }}>
                  {showNotes && (
                    <FontAwesomeIcon
                      icon={faMessageCaptions}
                      className="notesIcon"
                      onClick={() => setOpenNotesItem(service)}
                      style={{
                        cursor: "pointer",
                        color: service?.notes?.length ? "#CC672F" : "#8F8F8F",
                      }}
                    />
                  )}
                  {showSettings && menuItems?.length > 0 && (
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon
                        icon={faGear}
                        onClick={() =>
                          //@ts-ignore
                          setOpenMenuItem((prev) =>
                            prev?.id === service?.id ? null : service
                          )
                        }
                        style={{ cursor: "pointer" }}
                      />
                      {openMenuItem?.type?.includes("services") &&
                        openMenuItem?.id == service.id && (
                          <div className="cogMenu service">
                            {menuItems.map((item) => {
                              return (
                                <div
                                  className={`cogMenuItem ${
                                    item.label.includes("Standard")
                                      ? "standard"
                                      : ""
                                  }`}
                                  onClick={item.onClick}
                                >
                                  {item.label}
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </td>
        );
      })}
    </tr>
  );
};

const SubserviceTable = ({
  subservices,
  columnDefs,
  draggable,
  selectable,
  onReorderSubservices,
  onSelect,
  showSettings,
  setShowDeleteServiceModal,
  openMenuItem,
  setOpenMenuItem,
}: {
  subservices: Subservice[];
  columnDefs: ColumnDef<Service>[];
  draggable: boolean;
  selectable: boolean;
  onReorderSubservices: (newOrder: Subservice[]) => void;
  onSelect: (e, subId) => void;
  showSettings: boolean;
  setShowDeleteServiceModal: (bool: boolean) => void;
  openMenuItem: Subservice | null;
  setOpenMenuItem: (service) => void;
}) => {
  const [activeSubservice, setActiveSubservice] =
    useState<Subservice | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const sub = subservices.find(
      (s) => `subservice:${s.id}` === event.active.id
    );
    if (sub) setActiveSubservice(sub);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveSubservice(null);
      return;
    }

    const oldIndex = subservices.findIndex(
      (s) => `subservice:${s.id}` === active.id
    );
    const newIndex = subservices.findIndex(
      (s) => `subservice:${s.id}` === over.id
    );

    const reordered = arrayMove(subservices, oldIndex, newIndex).map(
      (s, i) => ({
        ...s,
        position: i + 1,
      })
    );
    onReorderSubservices(reordered);
    setActiveSubservice(null);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={subservices.map((s) => `subservice:${s.id}`)}
        strategy={verticalListSortingStrategy}
      >
        <table className={`serviceTable `}>
          <thead>
            <tr>
              {columnDefs.map((col) => (
                <th
                  key={col.id}
                  className={`tableCol ${toCamelCase(col.id)}Col`}
                >
                  {col.header()}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {subservices.map((sub, i) => (
              <SubserviceRow
                key={sub.id}
                subservice={sub}
                columnDefs={columnDefs}
                draggable={draggable}
                selectable={selectable}
                onSelect={onSelect}
                striped={i % 2 ? true : false}
                setShowDeleteServiceModal={setShowDeleteServiceModal}
                showSettings={showSettings}
                openMenuItem={openMenuItem}
                setOpenMenuItem={setOpenMenuItem}
              />
            ))}
          </tbody>
        </table>
      </SortableContext>
      <DragOverlay adjustScale={false}>
        {activeSubservice && (
          <ServiceOverlay
            service={activeSubservice as Service}
            columnDefs={columnDefs}
            selectable={selectable}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

const SubserviceRow = ({
  subservice,
  columnDefs,
  draggable,
  selectable,
  onSelect,
  striped,
  showSettings,
  setShowDeleteServiceModal,
  openMenuItem,
  setOpenMenuItem,
}: {
  subservice: Subservice;
  columnDefs: ColumnDef<Service>[];
  draggable: boolean;
  selectable: boolean;
  onSelect: (e, id) => void;
  striped: boolean;
  showSettings: boolean;
  setShowDeleteServiceModal: (bool: boolean) => void;
  openMenuItem: Subservice | null;
  setOpenMenuItem: (service) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: `subservice:${subservice.id}` });

  const isPlaceholder = subservice.id == "-1";

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        backgroundColor: striped ? "#FAFAFA" : "#fff",
      }}
    >
      {columnDefs.map((col, i) => {
        const isLast = i === columnDefs.length - 1;
        return (
          <td
            key={col.id}
            className={col?.id?.includes("name") ? "nameField" : ""}
          >
            <div className="serviceTd">
              {i === 0 && !isPlaceholder && (
                <>
                  {draggable && (
                    <span {...attributes} {...listeners} className="dndHandle">
                      <FontAwesomeIcon icon={faGripDotsVertical} />
                    </span>
                  )}
                  {selectable && (
                    <input
                      type="checkbox"
                      style={{ accentColor: "#418172" }}
                      checked={subservice.checked}
                      onChange={(e) => onSelect(e, subservice.id)}
                    />
                  )}
                </>
              )}
              {col.cell(subservice as Service)}
              {isLast && !isPlaceholder && showSettings && (
                <div style={{ position: "relative", paddingLeft: "5px" }}>
                  <>
                    <FontAwesomeIcon
                      icon={faGear}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuItem((prev) =>
                          prev?.id == subservice?.id ? null : subservice
                        );
                      }}
                      style={{ cursor: "pointer" }}
                    />
                    {openMenuItem?.type?.includes("subservices") &&
                      openMenuItem?.id == subservice.id && (
                        <div className="cogMenu service">
                          <div
                            className="cogMenuItem"
                            onClick={(e) => setShowDeleteServiceModal(true)}
                          >
                            Delete
                          </div>
                        </div>
                      )}
                  </>
                </div>
              )}
            </div>
          </td>
        );
      })}
    </tr>
  );
};
